import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import styled, { css } from 'styled-components';

export const formatPhoneForDisplay = (value, callingCode = '1') => {
  const digits = (value || '').replace(/\D/g, '');
  if ((callingCode || '1') !== '1') {
    return `+${callingCode} ${digits}`;
  }

  const usDigits = digits.slice(0, 10);
  if (usDigits.length <= 3) return usDigits;
  if (usDigits.length <= 6)
    return `(${usDigits.slice(0, 3)}) ${usDigits.slice(3)}`;
  return `(${usDigits.slice(0, 3)}) ${usDigits.slice(3, 6)}-${usDigits.slice(
    6
  )}`;
};

const getLocalPhoneDigits = (value, activeCode) => {
  let localPart = value.replace(new RegExp(`^\\+${activeCode}`), '');
  if (localPart === value) {
    localPart = value.replace(/^\+\d{1,4}/, '');
  }
  return localPart.replace(/\D/g, '');
};

const PhoneInputWrapper = styled.div`
  --phone-input-fill: rgba(255, 255, 255, 0.05);

  .PhoneInput {
    display: flex;
    align-items: center;
    border-radius: 8px;
    overflow: hidden;
  }

  .PhoneInputCountry {
    margin: 0;
    color: #fff;
    background: transparent;
    border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .PhoneInputCountry:focus-within {
    box-shadow: none;
    border: none;
  }

  .PhoneInputCountrySelect {
    background: transparent;
    color: #fff;
    border: none;
    outline: none;
    appearance: none;
    font-family: inherit;
    cursor: pointer;
  }

  .PhoneInputCountryIcon {
    width: 20px;
    height: auto;
  }

  .PhoneInputCountrySelectArrow {
    border-top-color: rgba(255, 255, 255, 0.8);
    opacity: 1;
  }

  .PhoneInputInput {
    flex: 1;
    border: none;
    outline: none;
    font-family: inherit;
    background: transparent;
    color: #fff;
    margin: 0;
    box-shadow: none;
    min-width: 0;
    text-align: left;
  }

  .PhoneInputInput:focus {
    box-shadow: none;
  }

  .PhoneInputInput:-webkit-autofill,
  .PhoneInputInput:-webkit-autofill:hover,
  .PhoneInputInput:-webkit-autofill:focus,
  .PhoneInputInput:-webkit-autofill:active {
    -webkit-text-fill-color: #fff;
    caret-color: #fff;
    -webkit-box-shadow: 0 0 0 1000px var(--phone-input-fill) inset;
    box-shadow: 0 0 0 1000px var(--phone-input-fill) inset;
    transition: background-color 9999s ease-in-out 0s;
  }

  ${(props) =>
    props.$variant === 'split'
      ? css`
          --phone-input-fill: rgba(255, 255, 255, 0.06);

          .PhoneInput {
            background: rgba(255, 255, 255, 0.06);
            border: 1px solid rgba(255, 255, 255, 0.12);
          }

          .PhoneInput:focus-within {
            border-color: #fa4616;
          }

          .PhoneInputCountry {
            padding: 0 12px;
          }

          .PhoneInputCountrySelect {
            font-size: 16px;

            @media (max-width: 900px) {
              font-size: 17px;
            }
          }

          .PhoneInputInput {
            font-size: 30px;
            line-height: 1.2;
            height: 56px;
            box-sizing: border-box;
            padding: 0 16px;
            transform: none;

            @media (max-width: 900px) {
              font-size: 31px;
            }
          }

          .PhoneInputInput::placeholder {
            color: #475569;
            font-size: 30px;
            line-height: 1.2;
            letter-spacing: 0;
            word-spacing: normal;

            @media (max-width: 900px) {
              font-size: 31px;
            }
          }

          @media (max-width: 760px) {
            --phone-input-fill: rgba(255, 255, 255, 0.08);

            .PhoneInput {
              background: rgba(255, 255, 255, 0.08);
              border-color: rgba(255, 255, 255, 0.2);
            }

            .PhoneInput:focus-within {
              box-shadow: 0 0 0 2px rgba(250, 70, 22, 0.2);
            }

            .PhoneInputInput::placeholder {
              color: #5a6a80;
            }
          }
        `
      : css`
          --phone-input-fill: rgba(255, 255, 255, 0.05);

          .PhoneInput {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.15);
          }

          .PhoneInput:focus-within {
            border-color: #fa4616;
          }

          .PhoneInputCountry {
            padding: 14px 12px;
          }

          .PhoneInputCountrySelect {
            font-size: 16px;

            @media (max-width: 900px) {
              font-size: 17px;
            }
          }

          .PhoneInputInput {
            font-size: 30px;
            line-height: 1.2;
            height: 56px;
            box-sizing: border-box;
            padding: 0 16px;
            transform: none;

            @media (max-width: 900px) {
              font-size: 31px;
            }
          }

          .PhoneInputInput::placeholder {
            color: #64748b;
            font-size: 30px;
            line-height: 1.2;
            letter-spacing: 0;
            word-spacing: normal;

            @media (max-width: 900px) {
              font-size: 31px;
            }
          }

          @media (max-width: 760px) {
            --phone-input-fill: rgba(255, 255, 255, 0.08);

            .PhoneInput {
              background: rgba(255, 255, 255, 0.08);
              border-color: rgba(255, 255, 255, 0.22);
            }

            .PhoneInput:focus-within {
              box-shadow: 0 0 0 2px rgba(250, 70, 22, 0.2);
            }

            .PhoneInputInput::placeholder {
              color: #6b7a90;
            }
          }
        `}
`;

const AuthPhoneInput = ({
  phoneNumber,
  countryCode,
  onPhoneNumberChange,
  onCountryCodeChange,
  placeholder,
  defaultCountry,
  variant,
}) => {
  const [phoneNumberFull, setPhoneNumberFull] = useState(
    phoneNumber ? `+${countryCode || '1'}${phoneNumber}` : ''
  );

  useEffect(() => {
    const nextValue = phoneNumber ? `+${countryCode || '1'}${phoneNumber}` : '';
    setPhoneNumberFull((currentValue) =>
      currentValue === nextValue ? currentValue : nextValue
    );
  }, [phoneNumber, countryCode]);

  return (
    <PhoneInputWrapper $variant={variant}>
      <PhoneInput
        placeholder={placeholder}
        defaultCountry={defaultCountry}
        international={false}
        useNationalFormatForDefaultCountryValue
        value={phoneNumberFull}
        onCountryChange={(nextCountry) => {
          if (!nextCountry) return;
          const nextCode = getCountryCallingCode(nextCountry);
          onCountryCodeChange(nextCode);
          setPhoneNumberFull(
            phoneNumber ? `+${nextCode}${phoneNumber}` : `+${nextCode}`
          );
        }}
        onChange={(value) => {
          const nextValue = value || '';
          setPhoneNumberFull(nextValue);

          if (!nextValue) {
            onPhoneNumberChange('');
            return;
          }

          const activeCode = countryCode || '1';
          onPhoneNumberChange(getLocalPhoneDigits(nextValue, activeCode));
        }}
      />
    </PhoneInputWrapper>
  );
};

AuthPhoneInput.propTypes = {
  phoneNumber: PropTypes.string,
  countryCode: PropTypes.string,
  onPhoneNumberChange: PropTypes.func.isRequired,
  onCountryCodeChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  defaultCountry: PropTypes.string,
  variant: PropTypes.oneOf(['glass', 'split']),
};

AuthPhoneInput.defaultProps = {
  phoneNumber: '',
  countryCode: '1',
  placeholder: '(555) 123-4567',
  defaultCountry: 'US',
  variant: 'glass',
};

export default AuthPhoneInput;
